import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  createPipeline,
  createSection,
  createStage,
  deletePipeline,
  deleteSection,
  deleteStage,
  updatePipeline,
  updateSection,
  updateStage,
  type PipelineInput,
  type SectionInput,
  type StageInput,
} from '@/api/pipelines'
import type { Stage } from '@/api/types'

/**
 * Backend PUT /api/stages/{id} replaces the whole stage, so every update must carry
 * all fields. Build the payload from the current stage and override what changed.
 */
export function stageToInput(s: Stage, overrides: Partial<StageInput> = {}): StageInput {
  return {
    sectionId: s.sectionId,
    name: s.name,
    shortName: s.shortName,
    color: s.color,
    position: s.position,
    stageType: s.stageType,
    autoAdvance: s.autoAdvance,
    advanceAfterDays: s.advanceAfterDays,
    advanceOnNextContact: s.advanceOnNextContact,
    requiredFields: s.requiredFields,
    ...overrides,
  }
}

function invalidatePipeline(qc: QueryClient, pipelineId?: string) {
  qc.invalidateQueries({ queryKey: ['pipeline', pipelineId] })
  qc.invalidateQueries({ queryKey: ['pipelines'] })
  qc.invalidateQueries({ queryKey: ['deals', 'board', pipelineId] })
  qc.invalidateQueries({ queryKey: ['attention'] })
  // A renamed stage is denormalised into every linked contact's Deals list.
  qc.invalidateQueries({ queryKey: ['contact-deals'] })
}

export function useCreatePipeline() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: PipelineInput) => createPipeline(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipelines'] }),
  })
}

/**
 * Callers must move the UI selection off the pipeline BEFORE mutating: removing the
 * caches below while a component still observes them would trigger a refetch → 404.
 */
export function useDeletePipeline() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deletePipeline(id),
    onSuccess: (_data, id) => {
      // drop caches keyed by the deleted pipeline so nothing refetches a 404
      qc.removeQueries({ queryKey: ['pipeline', id] })
      qc.removeQueries({ queryKey: ['deals', 'board', id] })
      qc.invalidateQueries({ queryKey: ['pipelines'] })
      qc.invalidateQueries({ queryKey: ['attention'] })
    },
  })
}

export function useUpdatePipeline(pipelineId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: PipelineInput) => updatePipeline(pipelineId as string, input),
    onSuccess: () => invalidatePipeline(qc, pipelineId),
  })
}

// --- sections ---

export function useCreateSection(pipelineId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SectionInput) => createSection(pipelineId as string, input),
    onSuccess: () => invalidatePipeline(qc, pipelineId),
  })
}

export function useUpdateSection(pipelineId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SectionInput }) => updateSection(id, input),
    onSuccess: () => invalidatePipeline(qc, pipelineId),
  })
}

export function useDeleteSection(pipelineId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteSection(id),
    onSuccess: () => invalidatePipeline(qc, pipelineId),
  })
}

/** Position swaps need several PUTs; run them as one mutation so the UI refreshes once. */
export function useReorderSections(pipelineId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (updates: { id: string; input: SectionInput }[]) =>
      Promise.all(updates.map((u) => updateSection(u.id, u.input))),
    onSuccess: () => invalidatePipeline(qc, pipelineId),
  })
}

// --- stages ---

export function useCreateStage(pipelineId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: StageInput) => createStage(pipelineId as string, input),
    onSuccess: () => invalidatePipeline(qc, pipelineId),
  })
}

export function useUpdateStage(pipelineId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: StageInput }) => updateStage(id, input),
    onSuccess: () => invalidatePipeline(qc, pipelineId),
  })
}

export function useDeleteStage(pipelineId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteStage(id),
    onSuccess: () => invalidatePipeline(qc, pipelineId),
  })
}

/** Position swaps need several PUTs; run them as one mutation so the UI refreshes once. */
export function useReorderStages(pipelineId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (updates: { id: string; input: StageInput }[]) =>
      Promise.all(updates.map((u) => updateStage(u.id, u.input))),
    onSuccess: () => invalidatePipeline(qc, pipelineId),
  })
}
